import React, { ReactNode, useRef } from 'react';
import { motion, useReducedMotion, useScroll, useTransform } from 'framer-motion';
import { MotionMode } from '../types/motion';
interface CinematicSceneProps {
  id: string;
  children: ReactNode;
  /** Full-bleed photograph for the frame. Omit when passing a backgroundSlot. */
  image?: string;
  imageAlt?: string;
  /** Live background layer (e.g. a map) rendered instead of a photograph. */
  backgroundSlot?: ReactNode;
  motionMode?: MotionMode;
  /** Direction of the dark scrim that keeps the pinned type legible. */
  scrim?: 'left' | 'right' | 'center' | 'none';
  /** Total scroll length of the frame. Longer frames hold the pin for longer. */
  height?: 'tall' | 'standard';
  className?: string;
}
const scrims: Record<'left' | 'right' | 'center' | 'none', string> = {
  left: 'bg-[linear-gradient(90deg,rgba(7,5,3,0.94)_0%,rgba(7,5,3,0.6)_45%,rgba(7,5,3,0.1)_80%)]',
  right: 'bg-[linear-gradient(270deg,rgba(7,5,3,0.94)_0%,rgba(7,5,3,0.6)_45%,rgba(7,5,3,0.1)_80%)]',
  center: 'bg-[radial-gradient(ellipse_at_center,rgba(7,5,3,0.55)_0%,rgba(7,5,3,0.82)_55%,rgba(7,5,3,0.96)_100%)]',
  none: ''
};
export function CinematicScene({
  id,
  children,
  image,
  imageAlt = '',
  backgroundSlot,
  motionMode = 'cinematic',
  scrim = 'center',
  height = 'standard',
  className = ''
}: CinematicSceneProps) {
  const ref = useRef<HTMLDivElement>(null);
  const reduced = useReducedMotion();
  const {
    scrollYProgress
  } = useScroll({
    target: ref,
    offset: ['start end', 'end start']
  });
  const animate = motionMode !== 'still' && !reduced;
  const drift = motionMode === 'subtle' ? 5 : 11;
  const y = useTransform(scrollYProgress, [0, 1], [`-${drift}%`, `${drift}%`]);
  const scale = useTransform(scrollYProgress, [0, 0.5, 1], [1.26, 1.1, 1.26]);
  const opacity = useTransform(scrollYProgress, [0, 0.12, 0.88, 1], [0.35, 1, 1, 0.35]);
  return <section id={id} ref={ref} className={`relative ${height === 'tall' ? 'h-[190vh]' : 'h-[165vh]'} ${className}`} data-id="element-14" data-mp="pcxe0vz7f7wc">
      <div className="sticky top-0 h-screen w-full overflow-hidden bg-ink" data-id="element-15" data-mp="3j2atb6uukus0">
        {backgroundSlot ? <div className="absolute inset-0" data-id="element-16" data-mp="3s4si9t1w01ip">{backgroundSlot}</div> : <motion.div aria-hidden="true" className="absolute -inset-[12%]" style={animate ? {
        y,
        scale,
        opacity
      } : {
        scale: 1.08,
        opacity: 1
      }} data-id="element-17" data-mp="3s2pgycvaalyo">
            <img src={image} alt={imageAlt} className="h-full w-full object-cover" loading={id === 'ritual' ? 'eager' : 'lazy'} data-id="element-18" data-mp="5oy2g36xvqoa" />
          </motion.div>}

        <div aria-hidden="true" className={`pointer-events-none absolute inset-0 ${scrims[scrim]}`} data-id="element-19" data-mp="jp0hzirry80d" />
        <div aria-hidden="true" className="scene-vignette pointer-events-none absolute inset-0" data-id="element-20" data-mp="2gi24v9tamz34" />
        <div aria-hidden="true" className="film-grain pointer-events-none absolute inset-0 opacity-60 mix-blend-overlay" data-id="element-21" data-mp="2lgvl39zyt46z" />
        <div aria-hidden="true" className="pointer-events-none absolute inset-5 border border-cream/10 md:inset-9" data-id="element-22" data-mp="296e0qgahdc2n" />

        <div className={`relative z-10 h-full w-full ${backgroundSlot ? 'pointer-events-none' : ''}`} data-id="element-23" data-mp="247kmumra7j1y">
          {children}
        </div>
      </div>
    </section>;
}